/**
 * Persistent right rail of the Admin User Editor (AR-327) — admin actions,
 * admin notes, and the read-only connected-record IDs. Visible on every tab.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DeferredAction, PendingIntegration, RecordCard } from './recordPrimitives';
import { EM_DASH, externalIdFor } from '@/lib/adminUserRecordApi';
import type { AdminUserRecord } from '@/lib/adminUserRecordApi';
import { primaryTramsAccount } from '@/lib/adminUserRecordApi';

type ActionKey =
  | 'change-email'
  | 'reset-email'
  | 'reset-token'
  | 'temp-password'
  | 'reset-launchpad'
  | 'terminate'
  | 'add-note'
  | null;

const ACTIONS: { key: Exclude<ActionKey, null>; label: string; destructive?: boolean }[] = [
  { key: 'change-email', label: "Change User's Account Email" },
  { key: 'reset-email', label: 'Send Password Reset Email' },
  { key: 'reset-token', label: 'Generate Password Reset Token' },
  { key: 'temp-password', label: 'Assign Temporary Password' },
  { key: 'reset-launchpad', label: 'Reset Launchpad Status' },
  { key: 'terminate', label: 'Terminate User', destructive: true },
];

export function RecordSidebar({ record }: { record: AdminUserRecord }) {
  const [action, setAction] = useState<ActionKey>(null);
  const [note, setNote] = useState('');
  const trams = primaryTramsAccount(record);

  const connectedRecords: { label: string; value: string }[] = [
    { label: 'AIVIA User ID', value: String(record.user.id) },
    ...(record.migrated
      ? [{ label: 'AIVIA UUID', value: record.migrated.user.uuid ?? EM_DASH }]
      : []),
    { label: 'WorldVia PRO ID', value: record.user.worldviaUserId ?? EM_DASH },
    { label: 'HubSpot Contact', value: externalIdFor(record, 'hubspot_contact') ?? EM_DASH },
    {
      label: 'Stripe Customer',
      value:
        record.subscription?.stripeCustomerId ??
        record.user.stripeCustomerId ??
        externalIdFor(record, 'stripe_customer') ??
        EM_DASH,
    },
    { label: 'TRAMS Interface ID', value: trams?.interfaceId ?? EM_DASH },
  ];

  return (
    <div className="flex w-full flex-col gap-5 2xl:w-[340px] 2xl:shrink-0">
      <RecordCard title="Admin Actions" description="Available on every tab">
        <div className="flex flex-col gap-2">
          {ACTIONS.map((item) => (
            <Button
              key={item.key}
              variant={item.destructive ? 'outline' : 'default'}
              className={
                item.destructive
                  ? 'justify-start border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-900 dark:text-rose-400'
                  : 'justify-start'
              }
              onClick={() => setAction(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </RecordCard>

      <RecordCard title="Admin Notes">
        <PendingIntegration source="the admin notes store" />
        <Textarea
          aria-label="Add a note"
          placeholder="Add a note…"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="mt-3 min-h-[70px] text-sm"
        />
        <Button
          size="sm"
          variant="outline"
          className="mt-2"
          disabled={note.trim().length === 0}
          onClick={() => setAction('add-note')}
        >
          Add Note
        </Button>
      </RecordCard>

      <RecordCard title="Connected Records" description="Read-only">
        <dl className="flex flex-col gap-2">
          {connectedRecords.map((item) => (
            <div key={item.label} className="flex items-baseline justify-between gap-3">
              <dt className="text-xs text-slate-500 dark:text-slate-400">{item.label}</dt>
              <dd className="truncate font-mono text-xs font-semibold text-slate-900 dark:text-slate-100">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </RecordCard>

      <DeferredAction
        open={action === 'change-email'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Change account email"
        description={`Replaces ${record.user.email} as the member's sign-in address and syncs the change to HubSpot.`}
        confirmLabel="Change email"
        fields={[{ name: 'email', label: 'New account email', type: 'email' }]}
        ticketNote="The email-change workflow is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'reset-email'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Send password reset email"
        description={`Sends a reset link to ${record.user.email}.`}
        confirmLabel="Send email"
        ticketNote="Credential actions are a companion backend ticket."
      />
      <DeferredAction
        open={action === 'reset-token'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Generate password reset token"
        description="Produces a one-time reset token to read out to the member. The token is shown once."
        confirmLabel="Generate token"
        ticketNote="Credential actions are a companion backend ticket."
      />
      <DeferredAction
        open={action === 'temp-password'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Assign temporary password"
        description="Sets a temporary password the member must change at next sign-in."
        confirmLabel="Assign password"
        fields={[{ name: 'password', label: 'Temporary password' }]}
        ticketNote="Credential actions are a companion backend ticket."
      />
      <DeferredAction
        open={action === 'reset-launchpad'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Reset Launchpad status"
        description="Clears the member's Launchpad progress so they restart onboarding from the first step."
        confirmLabel="Reset Launchpad"
        destructive
        ticketNote="Launchpad reset is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'terminate'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Terminate user"
        description="Ends the membership, revokes access, and unpublishes the member's Advisor Showcase. This cannot be undone from this screen."
        confirmLabel="Terminate user"
        destructive
        confirmPhrase={record.user.email}
        ticketNote="Termination is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'add-note'}
        onOpenChange={(open) => {
          if (!open) {
            setAction(null);
            setNote('');
          }
        }}
        title="Add admin note"
        description="Notes are attributed to you and visible to every admin on this record."
        confirmLabel="Add note"
        ticketNote="Admin notes storage is a companion backend ticket."
      />
    </div>
  );
}
